import React from "react";
import { X } from "lucide-react";

export function PageHeader({ eyebrow, title, description, actions, children }) {
  return <header className="flex items-start justify-between gap-4 flex-wrap" data-ui="page-header">
    <div className="min-w-0">
      {eyebrow && <div className="text-overline">{eyebrow}</div>}
      <h1 className="text-2xl font-light text-[#2D312E]">{title}</h1>
      {description && <p className="text-sm text-[#5C635E] mt-1 max-w-3xl">{description}</p>}
      {children}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </header>;
}

export function ActionButton({ children, variant = "secondary", busy = false, disabled, className = "", ...props }) {
  const styles = {
    primary: "bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white border-[#4A5D4E]",
    secondary: "bg-white hover:border-[#4A5D4E] text-[#2D312E] border-[#EAE6DF]",
    danger: "bg-[#C05746] hover:bg-[#A9483A] text-white border-[#C05746]",
    warning: "bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-300",
    ghost: "bg-transparent hover:bg-[#F4F1EA] text-[#5C635E] border-transparent",
  };
  return <button type="button" disabled={disabled || busy} aria-busy={busy || undefined}
    className={`ui-action inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${styles[variant] || styles.secondary} ${className}`}
    {...props}>{children}</button>;
}

const STATUS = {
  verified: ["Verified", "✓", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  approved: ["Verified", "✓", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  unverified: ["Pending verification", "○", "bg-amber-50 text-amber-800 border-amber-200"],
  pending: ["Pending", "○", "bg-amber-50 text-amber-800 border-amber-200"],
  rejected: ["Rejected", "×", "bg-red-50 text-red-800 border-red-200"],
  clash: ["Clash", "!", "bg-red-50 text-red-800 border-red-200"],
  repeated: ["Repeated", "↻", "bg-blue-50 text-blue-800 border-blue-200"],
  present: ["Present", "✓", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  full_day: ["Full day", "✓", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  absent: ["Absent", "×", "bg-red-50 text-red-800 border-red-200"],
  late: ["Late", "!", "bg-amber-50 text-amber-800 border-amber-200"],
  half_day: ["Half day", "½", "bg-orange-50 text-orange-800 border-orange-200"],
  office_off: ["Office off", "—", "bg-slate-50 text-slate-700 border-slate-200"],
  holiday: ["Holiday", "—", "bg-slate-50 text-slate-700 border-slate-200"],
  on_leave: ["On leave", null, "bg-violet-50 text-violet-800 border-violet-200"],
  upcoming: ["Upcoming", "○", "bg-slate-50 text-slate-600 border-slate-200"],
  finalized: ["Finalized", "✓", "bg-emerald-50 text-emerald-800 border-emerald-200"],
};

export function StatusBadge({ status, label, title, className = "" }) {
  const [defaultLabel, symbol, styles] = STATUS[status] || [label || status, "•", "bg-slate-50 text-slate-700 border-slate-200"];
  return <span title={title} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${styles} ${className}`}>
    {symbol && <span aria-hidden="true">{symbol}</span>}<span>{label || defaultLabel}</span>
  </span>;
}

export function FilterSummary({ filters, onClearAll }) {
  const active = (filters || []).filter((filter) => filter && filter.value !== "" && filter.value != null);
  if (!active.length) return null;
  return <div className="flex items-center gap-2 flex-wrap" aria-label="Active filters">
    <span className="text-xs font-medium text-[#5C635E]">Active filters:</span>
    {active.map((filter) => <button key={filter.key} type="button" onClick={filter.onRemove}
      className="inline-flex min-h-9 items-center gap-1 rounded-full border border-[#D8D4CC] bg-white px-3 py-1 text-xs text-[#2D312E] hover:border-[#C05746]"
      aria-label={`Remove ${filter.label} filter`}>
      {filter.label}: {filter.display ?? filter.value}<X className="h-3 w-3" />
    </button>)}
    {onClearAll && <button type="button" onClick={onClearAll} className="min-h-9 px-2 text-xs text-[#C05746] hover:underline">Clear all</button>}
  </div>;
}

export function AccessibleTable({ label, children, className = "" }) {
  return <table className={className} aria-label={label}><caption className="sr-only">{label}</caption>{children}</table>;
}
